import axios from 'axios';
import { DISCUSSION_API_URL, AUTH_API_URL } from '../../../config/env';
import {
  getAccessToken, getCsrfToken,
  setAccessToken, clearAccessToken,
  getRefreshingPromise, setRefreshingPromise, clearRefreshingPromise,
} from '../../../lib/tokenStore';

// The panels API is at /api/vc/v1/panels — directly via DISCUSSION_API_URL
const PANELS_API_URL = DISCUSSION_API_URL;

const panelsAxios = axios.create({
  withCredentials: true,
  baseURL: PANELS_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

panelsAxios.interceptors.request.use(
  (config) => {
    const csrf = getCsrfToken();
    if (csrf) config.headers['x-csrf-token'] = csrf;

    const token = getAccessToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

panelsAxios.interceptors.response.use(
  (response) => {
    const body = response.data;
    return body?.data !== undefined ? body.data : body;
  },
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || original._retried) return Promise.reject(error);
    original._retried = true;

    if (!getRefreshingPromise()) {
      const refreshPromise = (async () => {
        try {
          const csrf = getCsrfToken();
          const res = await axios.post(
            `${AUTH_API_URL}/refresh`,
            {},
            { withCredentials: true, headers: { ...(csrf ? { 'x-csrf-token': csrf } : {}) } }
          );
          setAccessToken(res.data.data.accessToken);
        } catch {
          clearAccessToken();
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('user');
          localStorage.removeItem('name');
          if (window.location.pathname !== '/login') window.location.href = '/login';
          throw error;
        } finally {
          clearRefreshingPromise();
        }
      })();
      setRefreshingPromise(refreshPromise);
    }
    await getRefreshingPromise();
    original.headers['Authorization'] = `Bearer ${getAccessToken()}`;
    return panelsAxios(original);
  }
);

// ── Panel CRUD ────────────────────────────────────────────────────────────────
export const getMyPanels = async () => {
  return panelsAxios.get('/mine');
};

export const getPanelByOpportunityId = async (opportunityId) => {
  return panelsAxios.get(`/opportunity/${opportunityId}`);
};

export const getPanelById = async (panelId) => {
  return panelsAxios.get(`/${panelId}`);
};

export const joinPanel = async (panelId) => {
  return panelsAxios.post(`/${panelId}/join`);
};

// ── Discussions ───────────────────────────────────────────────────────────────
export const getDiscussions = async ({ panelId, sort = 'recent', page = 1, limit = 3, tag, q }) => {
  return panelsAxios.get(`/${panelId}/discussions`, {
    params: { sort, page, limit, tag, q: q || undefined },
  });
};

export const createDiscussion = async ({ panelId, title, content, tags, imageUrl, images }) => {
  return panelsAxios.post(`/${panelId}/discussions`, { title, content, tags, imageUrl, images });
};

export const getDiscussionById = async ({ panelId, discussionId }) => {
  return panelsAxios.get(`/${panelId}/discussions/${discussionId}`);
};

export const toggleLike = async ({ panelId, discussionId }) => {
  return panelsAxios.post(`/${panelId}/discussions/${discussionId}/like`);
};

export const togglePin = async ({ panelId, discussionId }) => {
  return panelsAxios.post(`/${panelId}/discussions/${discussionId}/pin`);
};

export const flagDiscussion = async ({ panelId, discussionId }) => {
  return panelsAxios.post(`/${panelId}/discussions/${discussionId}/flag`);
};

export const addComment = async ({ panelId, discussionId, content, parentId }) => {
  return panelsAxios.post(`/${panelId}/discussions/${discussionId}/comments`, { content, parentId });
};

export const getComments = async ({ panelId, discussionId, page = 1, limit = 5 }) => {
  // Interceptor unwraps to { data: [...], meta: {...} }
  return panelsAxios.get(`/${panelId}/discussions/${discussionId}/comments`, {
    params: { page, limit },
  });
};

export const editComment = async ({ panelId, discussionId, commentId, content }) => {
  return panelsAxios.put(`/${panelId}/discussions/${discussionId}/comments/${commentId}`, { content });
};

export const deleteComment = async ({ panelId, discussionId, commentId }) => {
  return panelsAxios.delete(`/${panelId}/discussions/${discussionId}/comments/${commentId}`);
};

export const editDiscussion = async ({ panelId, discussionId, title, content, tags, images }) => {
  return panelsAxios.put(`/${panelId}/discussions/${discussionId}`, { title, content, tags, images });
};

export const deleteDiscussion = async ({ panelId, discussionId }) => {
  return panelsAxios.delete(`/${panelId}/discussions/${discussionId}`);
};

// ── Sidebar / Members ────────────────────────────────────────────────────────
export const getHotDiscussions = async (panelId) => {
  return panelsAxios.get(`/${panelId}/hot`);
};

export const getTrendingTopics = async (panelId) => {
  return panelsAxios.get(`/${panelId}/trending`);
};

export const getPanelMembers = async ({ panelId, search }) => {
  return panelsAxios.get(`/${panelId}/members`, {
    params: { search }
  });
};

// ── Search discoverable panels ─────────────────────────────────────────────────
export const searchPanels = async (q) => {
  return panelsAxios.get('/search', { params: { q, limit: 5 } });
};
